import PopupStickers from "components/admin/popups/stickers";
import { isActiveBidKeyFeaturesReview } from "components/bidKeyFeaturesReviewTour/types";
import { useUnreadItems } from "contexts/unreadItemsContext";
import add from "date-fns/add";
import isAfter from "date-fns/isAfter";
import { isImpersonatingUser } from "helpers/localStorage";
import { useLocalStorage } from "hooks/useLocalStorage";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import SignalRConnection from "services/signalRConnection";
import { getUserPopups } from "sources/books/popups";
import connect from "./container";

const BidKeyFeaturesReviewTour = lazy(() => import("components/bidKeyFeaturesReviewTour"));

function PopupNotifications({ profile, organization, roles, getOrg }) {
    const isOnboardingSuppressed = roles.isIntegrationAccount || isImpersonatingUser();
    const { setUnreadChatsCount, setUnreadItems } = useUnreadItems();
    const [popups, setPopups] = useState([]);
    const [bidKeyFeaturesReview, setBidKeyFeaturesReview] = useState(null);
    const [jivoInputEnabledBefore, setJivoInputEnabledBefore] = useLocalStorage("jivo-input-enabled", undefined);
    const jivoTimeout = useRef(null);
    const reviewFlowOpened = useRef(false);

    const deactivatePopup = ({ id }) => setPopups((items) => items.filter((item) => item.id !== id));
    const activatePopup = (newItem) => setPopups((items) => [...items, newItem]);
    const startJivoChat = () => {
        window.jivo_api?.sendMessage(`start ${Date.now()}`);
    };

    const disableJivoInput = () => {
        const contextNode = document.getElementById("jcont");
        const textarea = contextNode.getElementsByTagName("textarea")[0];
        const textareaContainer = textarea?.parentElement.parentElement.parentElement.parentElement.parentElement;
        if (textareaContainer) {
            textarea.setAttribute("disabled", true);
            textareaContainer.setAttribute("disabled", true);
        }
    };

    const enableJivoInput = () => {
        const contextNode = document.getElementById("jcont");
        const textarea = contextNode.getElementsByTagName("textarea")[0];
        const textareaContainer = textarea?.parentElement.parentElement.parentElement.parentElement.parentElement;
        if (textareaContainer) {
            textarea.removeAttribute("disabled");
            textareaContainer.removeAttribute("disabled");
        }
    };

    useEffect(() => {
        const previousJivoOnOpen = window.jivo_onOpen;
        const handleJivoOpen = function () {
            jivoTimeout.current = setTimeout(() => {
                const contextNode = document.getElementById("jcont");

                if (!jivoInputEnabledBefore || isAfter(new Date(), new Date(jivoInputEnabledBefore))) {
                    disableJivoInput();
                }
                const matchingElement = document.evaluate(
                    "//jdiv[contains(text(), 'start')]",
                    contextNode,
                    null,
                    XPathResult.ANY_TYPE,
                    null,
                );
                let counter = 0;
                while (true) {
                    const startTextEl = matchingElement.iterateNext();
                    if (!startTextEl || counter++ > 10) {
                        break;
                    }
                    startTextEl.parentElement.parentElement.parentElement.style.display = "none";
                }
            }, 500);
        };
        window.jivo_onOpen = handleJivoOpen;
        return () => {
            clearTimeout(jivoTimeout.current);
            if (window.jivo_onOpen === handleJivoOpen) {
                window.jivo_onOpen = previousJivoOnOpen;
            }
        };
    }, []);

    const setJivoChatInputStatus = (isEnabled) => {
        if (isEnabled) {
            const before = add(new Date(), { minutes: 30 });
            setJivoInputEnabledBefore(before);
            enableJivoInput();
        } else {
            setJivoInputEnabledBefore(undefined);
            disableJivoInput();
        }
    };

    const updateUnreadCarChatsCount = ({ unreadCount }) =>
        setUnreadItems((items) => ({ ...items, "car-chats": unreadCount }));

    const openBidKeyFeaturesReview = (review) => {
        if (isOnboardingSuppressed || !isActiveBidKeyFeaturesReview(review)) {
            return;
        }

        reviewFlowOpened.current = true;
        setBidKeyFeaturesReview(review);
    };

    const showBidKeyFeaturesReview = (review) => {
        if (isOnboardingSuppressed || (review?.userId && Number(review.userId) !== Number(profile.id))) {
            return;
        }

        openBidKeyFeaturesReview(review);
        getOrg?.();
    };

    useEffect(() => {
        const review = organization?.bidKeyFeaturesReview;

        if (isOnboardingSuppressed || !isActiveBidKeyFeaturesReview(review)) {
            reviewFlowOpened.current = false;
            setBidKeyFeaturesReview(null);
            return;
        }

        openBidKeyFeaturesReview(review);
    }, [isOnboardingSuppressed, organization?.bidKeyFeaturesReview?.bidId, organization?.bidKeyFeaturesReview?.stage]);

    useEffect(() => {
        getUserPopups().then(setPopups);
        const connection = new SignalRConnection({
            url: "/signalr/notifications",
            connectionParams: String(profile.id),
            actions: [
                { name: "Send", action: setUnreadChatsCount },
                { name: "SendUnreadCarChatsCount", action: updateUnreadCarChatsCount },
                { name: "ActivatePopUp", action: activatePopup },
                { name: "DeactivatePopUp", action: deactivatePopup },
                { name: "StartJivoChat", action: startJivoChat },
                { name: "SetJivoChatInputStatus", action: setJivoChatInputStatus },
                { name: "ShowBidKeyFeaturesReview", action: showBidKeyFeaturesReview },
            ],
            joinActionName: "Join",
            leaveActionName: "Leave",
        });
        return () => connection.disconnect();
    }, []);

    const closeBidKeyFeaturesReview = () => {
        reviewFlowOpened.current = false;
        setBidKeyFeaturesReview(null);
    };

    return (
        <>
            <PopupStickers items={popups} deactivatePopup={deactivatePopup} />
            {!isOnboardingSuppressed && bidKeyFeaturesReview && (
                <Suspense fallback={null}>
                    <BidKeyFeaturesReviewTour review={bidKeyFeaturesReview} onClose={closeBidKeyFeaturesReview} />
                </Suspense>
            )}
        </>
    );
}

export default connect(PopupNotifications);
